import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectValue,
} from "@/components/ui/select";
import { FilterChipTrigger } from "@/features/objectives/filter-chip-trigger";
import type { ObjectiveFilters } from "@/hooks/use-objectives";
import { useStrategicPlans } from "@/hooks/use-strategic-plans";
import { useFocusAreas } from "@/hooks/use-focus-areas";
import { useUsers } from "@/hooks/use-users";
import { useCurrentUser, isSupervisorOrAbove } from "@/hooks/use-current-user";
import { YEAR_OPTIONS } from "@/lib/year-options";
import { compareUsersByLastName, formatNameLastFirst } from "@/lib/format-name";

interface ObjectiveFiltersBarProps {
  filters: ObjectiveFilters;
  onFilterChange: (filters: ObjectiveFilters) => void;
}

export function ObjectiveFiltersBar({ filters, onFilterChange }: ObjectiveFiltersBarProps) {
  const { currentUser } = useCurrentUser();
  const showOwnerFilter = isSupervisorOrAbove(currentUser?.role);
  const { data: usersData } = useUsers(showOwnerFilter ? { limit: 100 } : { limit: 0 });
  const allUsers = usersData?.data ?? [];
  const { data: plansData } = useStrategicPlans();
  const { data: focusAreasData } = useFocusAreas();
  const plans = plansData?.data ?? [];
  const allFocusAreas = focusAreasData?.data ?? [];

  const focusAreasByPlan = plans.map((plan) => ({
    plan,
    focusAreas: allFocusAreas.filter((fa) => fa.strategic_plan_id === plan.id),
  })).filter((group) => group.focusAreas.length > 0);

  const setFilter = (patch: Partial<ObjectiveFilters>) => {
    onFilterChange({ ...filters, cursor: undefined, ...patch });
  };

  const clearFilter = (key: keyof ObjectiveFilters) => {
    const next = { ...filters, cursor: undefined };
    delete next[key];
    onFilterChange(next);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={filters.year?.toString() ?? "__all__"}
        onValueChange={(v) => v === "__all__" ? clearFilter("year") : setFilter({ year: parseInt(v, 10) })}
      >
        <FilterChipTrigger active={filters.year !== undefined} aria-label="Filter by year">
          <SelectValue placeholder="Year" />
        </FilterChipTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Years</SelectItem>
          {YEAR_OPTIONS.map((y) => (
            <SelectItem key={y} value={y.toString()}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.level ?? "__all__"}
        onValueChange={(v) => v === "__all__" ? clearFilter("level") : setFilter({ level: v as ObjectiveFilters["level"] })}
      >
        <FilterChipTrigger active={filters.level !== undefined} aria-label="Filter by level">
          <SelectValue placeholder="Level" />
        </FilterChipTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Levels</SelectItem>
          <SelectItem value="department">Department</SelectItem>
          <SelectItem value="team">Team</SelectItem>
          <SelectItem value="individual">Individual</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.status ?? "__all__"}
        onValueChange={(v) => v === "__all__" ? clearFilter("status") : setFilter({ status: v as ObjectiveFilters["status"] })}
      >
        <FilterChipTrigger active={filters.status !== undefined} aria-label="Filter by status">
          <SelectValue placeholder="Status" />
        </FilterChipTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Statuses</SelectItem>
          <SelectItem value="draft">Draft</SelectItem>
          <SelectItem value="approved">Approved</SelectItem>
          <SelectItem value="in_progress">In Progress</SelectItem>
          <SelectItem value="completed">Completed</SelectItem>
          <SelectItem value="killed">Killed</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.type ?? "__all__"}
        onValueChange={(v) => v === "__all__" ? clearFilter("type") : setFilter({ type: v as ObjectiveFilters["type"] })}
      >
        <FilterChipTrigger active={filters.type !== undefined} aria-label="Filter by type">
          <SelectValue placeholder="Type" />
        </FilterChipTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Types</SelectItem>
          <SelectItem value="committed">Committed</SelectItem>
          <SelectItem value="aspirational">Aspirational</SelectItem>
        </SelectContent>
      </Select>

      {showOwnerFilter && (
        <Select
          value={filters.owner_id ?? "__all__"}
          onValueChange={(v) => v === "__all__" ? clearFilter("owner_id") : setFilter({ owner_id: v })}
        >
          <FilterChipTrigger active={filters.owner_id !== undefined} aria-label="Filter by owner">
            <SelectValue placeholder="Owner" />
          </FilterChipTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Owners</SelectItem>
            {[...allUsers].sort(compareUsersByLastName).map((u) => (
              <SelectItem key={u.id} value={u.id}>{formatNameLastFirst(u.name)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Select
        value={filters.focus_area_id ?? "__all__"}
        onValueChange={(v) => v === "__all__" ? clearFilter("focus_area_id") : setFilter({ focus_area_id: v })}
      >
        <FilterChipTrigger
          active={filters.focus_area_id !== undefined}
          aria-label="Filter by ITS focus area"
        >
          <SelectValue placeholder="ITS Focus Area" />
        </FilterChipTrigger>
        <SelectContent>
          <SelectItem value="__all__">All ITS Focus Areas</SelectItem>
          {focusAreasByPlan.map((group) => (
            <SelectGroup key={group.plan.id}>
              <SelectLabel>{group.plan.name}</SelectLabel>
              {group.focusAreas.map((fa) => (
                <SelectItem key={fa.id} value={fa.id}>{fa.name}</SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
